import { OrganizationFeatureEnum, OrganizationFeatureStageEnum } from '../gen/cortado/v1/settings_pb'

// Stages that still gate real behavior. GA (graduated, no code consults the
// flag anymore) and RETIRED (removed) are dead and never offered for grant.
const LIVE_STAGES = new Set<OrganizationFeatureStageEnum>([
  OrganizationFeatureStageEnum.ORGANIZATION_FEATURE_STAGE_EXPERIMENTAL,
  OrganizationFeatureStageEnum.ORGANIZATION_FEATURE_STAGE_ENTITLEMENT,
  OrganizationFeatureStageEnum.ORGANIZATION_FEATURE_STAGE_TIER_MARKER,
])

export function isLiveStage(stage: OrganizationFeatureStageEnum): boolean {
  return LIVE_STAGES.has(stage)
}

export interface SplitFeatureCatalog {
  liveFeatures: OrganizationFeatureEnum[]
  deprecatedFeatures: OrganizationFeatureEnum[]
}

/**
 * Splits the feature catalog into the live flags that still gate something
 * (rendered in the main table) and the GA/RETIRED flags this org still has
 * an enabled row for (rendered in the collapsed Deprecated section). A
 * GA/RETIRED flag with no enabled row for this org is dropped entirely.
 */
export function splitFeatureCatalog(
  entries: { feature: OrganizationFeatureEnum; stage: OrganizationFeatureStageEnum }[],
  enabledFeatures: ReadonlySet<OrganizationFeatureEnum>,
): SplitFeatureCatalog {
  const liveFeatures: OrganizationFeatureEnum[] = []
  const deprecatedFeatures: OrganizationFeatureEnum[] = []
  for (const entry of entries) {
    if (isLiveStage(entry.stage)) {
      liveFeatures.push(entry.feature)
    } else if (enabledFeatures.has(entry.feature)) {
      deprecatedFeatures.push(entry.feature)
    }
  }
  return { liveFeatures, deprecatedFeatures }
}

/** Live flags not yet enabled for this org — what the "add feature" dropdown offers. */
export function availableFeaturesFor(
  liveFeatures: OrganizationFeatureEnum[],
  enabledFeatures: ReadonlySet<OrganizationFeatureEnum>,
): OrganizationFeatureEnum[] {
  return liveFeatures.filter((feature) => !enabledFeatures.has(feature))
}

/**
 * Features a "filter by feature" control should offer: live-stage flags only.
 * A GA/RETIRED flag gates nothing, so an org's leftover enabled row is not a
 * meaningful filter. Sorted by display label for a stable order.
 */
export function filterableFeatures(
  entries: { feature: OrganizationFeatureEnum; stage: OrganizationFeatureStageEnum }[],
): OrganizationFeatureEnum[] {
  return entries
    .filter((entry) => isLiveStage(entry.stage))
    .map((entry) => entry.feature)
    .sort((a, b) => featureLabel(a).localeCompare(featureLabel(b)))
}

/**
 * Rows for the "Feature hygiene" panel: GA/RETIRED flags (dead — no code
 * consults them anymore) that still have at least one org with an enabled
 * row. An Experimental/Entitlement/TierMarker flag is never dead even with a
 * nonzero count, and a GA/RETIRED flag with a zero count needs no cleanup.
 */
export function deadFeatureRows(
  entries: { feature: OrganizationFeatureEnum; stage: OrganizationFeatureStageEnum; enabledOrgCount: number }[],
): { feature: OrganizationFeatureEnum; stage: OrganizationFeatureStageEnum; enabledOrgCount: number }[] {
  return entries.filter((entry) => !isLiveStage(entry.stage) && entry.enabledOrgCount > 0)
}

/**
 * Summary line for the dashboard drift card, e.g. "2 GA/Retired flags still
 * have organization rows". Empty rows produce an empty string — callers
 * should not render the card at all in that case.
 */
export function featureDriftSummary(
  rows: { enabledOrgCount: number }[],
): string {
  if (!rows.length) return ''
  const flagWord = rows.length === 1 ? 'flag' : 'flags'
  const verb = rows.length === 1 ? 'has' : 'have'
  return `${rows.length} GA/Retired ${flagWord} still ${verb} organization rows`
}

// The backend catalog deliberately omits display labels; keep them here.
// A flag present in the catalog but missing here still renders via
// featureLabel()'s fallback below rather than blank or crashing.
const FEATURE_LABELS: Partial<Record<OrganizationFeatureEnum, string>> = {
  [OrganizationFeatureEnum.ORGANIZATION_FEATURE_EMAIL]: 'Email',
  [OrganizationFeatureEnum.ORGANIZATION_FEATURE_AI_TEMPLATES]: 'AI Templates',
  [OrganizationFeatureEnum.ORGANIZATION_FEATURE_QUOTING]: 'Quoting',
  [OrganizationFeatureEnum.ORGANIZATION_FEATURE_NOTIFIER]: 'Notifier',
  [OrganizationFeatureEnum.ORGANIZATION_FEATURE_FORM_SUBDOMAIN]: 'Form Subdomain',
  [OrganizationFeatureEnum.ORGANIZATION_FEATURE_EMAIL_INBOX]: 'Connected inbox',
  [OrganizationFeatureEnum.ORGANIZATION_FEATURE_REPORTING]: 'Reporting',
  [OrganizationFeatureEnum.ORGANIZATION_FEATURE_FOUNDER]: 'Founder',
  [OrganizationFeatureEnum.ORGANIZATION_FEATURE_COLLECTIVE]: 'Collective',
  [OrganizationFeatureEnum.ORGANIZATION_FEATURE_AI_AGENT]: 'AI Agent',
  [OrganizationFeatureEnum.ORGANIZATION_FEATURE_AI_AGENT_AUTOPILOT]: 'AI Agent Autopilot',
  [OrganizationFeatureEnum.ORGANIZATION_FEATURE_SUPPORT_CHAT]: 'Support Chat',
  [OrganizationFeatureEnum.ORGANIZATION_FEATURE_WORKFLOWS]: 'Workflows',
  [OrganizationFeatureEnum.ORGANIZATION_FEATURE_CUSTOM_PUBLIC_DOMAINS]: 'Custom Public Domains',
  [OrganizationFeatureEnum.ORGANIZATION_FEATURE_FORM_THEMING]: 'Form Theming',
  [OrganizationFeatureEnum.ORGANIZATION_FEATURE_QUOTE_ADDONS]: 'Quote Add-ons',
  [OrganizationFeatureEnum.ORGANIZATION_FEATURE_MCP]: 'MCP',
  [OrganizationFeatureEnum.ORGANIZATION_FEATURE_API_ACCESS]: 'API Access',
  [OrganizationFeatureEnum.ORGANIZATION_FEATURE_JOBS]: 'Jobs',
}

/** Prettifies an unmapped enum name, e.g. ORGANIZATION_FEATURE_FOO_BAR -> "Foo Bar". */
function fallbackFeatureLabel(feature: OrganizationFeatureEnum): string {
  const name = OrganizationFeatureEnum[feature]
  if (!name) return `Feature ${feature}`
  return name
    .replace(/^ORGANIZATION_FEATURE_/, '')
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ')
}

export function featureLabel(feature: OrganizationFeatureEnum): string {
  return FEATURE_LABELS[feature] ?? fallbackFeatureLabel(feature)
}
